import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useState } from 'react'
import { useDispatchAsync } from '@src/utils/dispatch'
import { KeyValueString, Product, PromotionCode, WorkflowsRunState } from '@src/types'
import { userSelector } from '@src/selectors/app'
import { Analytics } from '@src/config'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { CheckoutOneTimePaymentMutation } from '@src/screens/Marketplace/graphql/purchaseOneTimePayment.generated'
import { useRunWorkflow } from '@src/utils/workflows'
import { OneTimePaymentCheckoutQuery } from '../../graphql/oneTimePaymentCheckout.generated'

type SuccessfulCheckout = Omit<OneTimePaymentCheckoutQuery['oneTimePaymentCheckout'], 'invoice'> & {
  invoice: NonNullable<OneTimePaymentCheckoutQuery['oneTimePaymentCheckout']['invoice']>
}

interface PurchaseOneTimePaymentParams {
  product: Pick<Product, 'key'>
  quantity?: number
  promotionCode?: PromotionCode | null
  metadata?: Array<KeyValueString> | KeyValueString
  idempotencyKey?: string
}

interface CheckoutOneTimePaymentParams extends PurchaseOneTimePaymentParams {
  providerPaymentMethodId: string
}

export const usePurchaseOneTimePayment = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const runWorkflow = useRunWorkflow()

  const user = useSelector(userSelector)
  const { fullName, email, phoneNumber, paymentMethod } = user || {}
  const onboardingIsInProgress = useShouldShowPurchaseFlow()

  const checkoutOneTimePayment = useCallback(
    async ({
      product,
      providerPaymentMethodId,
      quantity,
      promotionCode,
      metadata,
    }: CheckoutOneTimePaymentParams) => {
      const {
        checkoutOneTimePayment: workflow,
      } = await dispatchAsync<CheckoutOneTimePaymentMutation>({
        type: 'marketplace/purchaseOneTimePayment',
        payload: {
          providerPaymentMethodId,
          providerPromotionCodeId: promotionCode?.providerId ?? null,
          purchasableItems: [
            {
              product: product.key,
              quantity: quantity ?? 1,
            },
          ],
          email,
          fullName,
          phoneNumber,
          metadata,
        },
      })
      return workflow
    },
    [dispatchAsync, email, fullName, phoneNumber],
  )

  const purchaseOneTimePayment = useCallback(
    async (params: PurchaseOneTimePaymentParams) => {
      try {
        if (!paymentMethod) {
          throw new Error('Payment method required')
        }
        setLoading(true)

        const run = await runWorkflow(
          () =>
            checkoutOneTimePayment({
              ...params,
              providerPaymentMethodId: paymentMethod.stripeId,
            }),
          { idempotencyKey: params.idempotencyKey },
        )

        if (run.state === WorkflowsRunState.Failed) {
          throw new Error(run.errorMessage ?? 'Payment failed')
        }

        const {
          oneTimePaymentCheckout: checkout,
        } = await dispatchAsync<OneTimePaymentCheckoutQuery>({
          type: 'marketplace/oneTimePaymentCheckout',
          payload: { id: run.subjectId },
        })

        if (!checkout.invoice) {
          // This shouldn't happen in practice if the workflow run was successful
          throw new Error('Payment failed')
        }

        Analytics.trackPurchase({
          productId: checkout.invoice.primaryProduct.id,
          revenue: checkout.invoice.total || 0,
          nickname: checkout.invoice.primaryProduct.title,
          onboarding: onboardingIsInProgress,
          transactionId: checkout.invoice.stripeId,
        })

        dispatch({ type: 'uiFlags/fetch' })
        await dispatchAsync({ type: 'users/fetch' })

        return checkout as SuccessfulCheckout
      } catch (error: any) {
        setError(error.message)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [
      paymentMethod,
      runWorkflow,
      dispatchAsync,
      onboardingIsInProgress,
      dispatch,
      checkoutOneTimePayment,
    ],
  )

  return { purchaseOneTimePayment, loading, error }
}
